import { alert } from '@/services/alert';
import { fetchBot } from '@/services/services';
import { CARECREDIT_URL, PacAction, PATIENTFI_URL } from '@bariendo/lib/patient-access-script';
import { getReferenceString } from '@medplum/core';
import { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useState } from 'react';
import type { SendSmsParams } from '../../../../../../bots/src/handlers/send-sms-message';

const EMPLOYER_COVERAGE_URL = 'https://everself.com/coverage/';

/**
 * Cherry financing application. Unset by default on purpose: SMS-ing a guessed
 * URL to a lead is worse than not sending, so the action disables itself until
 * the real link is configured.
 */
const CHERRY_APPLICATION_URL: string | undefined = import.meta.env.VITE_CHERRY_APPLICATION_URL;

/** The other financing partners, opened for the navigator in a new tab (BAR-2923). */
const PARTNER_LINKS: Partial<Record<PacAction, string>> = {
  openPatientFi: PATIENTFI_URL,
  openCareCredit: CARECREDIT_URL,
};

/**
 * The financing pivots the intro call and the PN consultation share: open the
 * employer-coverage checker, text the patient the Cherry application, and open
 * PatientFi or CareCredit.
 *
 * Any other action is ignored here, so a card with pivots of its own — the
 * hand-off's payment order — wraps this rather than re-implementing the pair.
 */
export function usePatientAccessActions(patient: Patient | undefined): {
  /** Resolves true once a Cherry link is sent; false for anything that didn't send. */
  handleAction: (action: PacAction) => Promise<boolean>;
  pendingAction: PacAction | undefined;
} {
  const medplum = useMedplum();
  const [pendingAction, setPendingAction] = useState<PacAction | undefined>();

  const handleAction = useCallback(
    async (action: PacAction) => {
      if (action === 'checkEmployerCoverage') {
        window.open(EMPLOYER_COVERAGE_URL, '_blank', 'noopener');
        return false;
      }

      const partnerUrl = PARTNER_LINKS[action];
      if (partnerUrl) {
        window.open(partnerUrl, '_blank', 'noopener');
        return false;
      }

      if (action !== 'sendCherryLink') return false;

      if (!CHERRY_APPLICATION_URL) {
        alert.error('The Cherry application link is not configured yet. Send it manually for now.');
        return false;
      }

      const phone = patient?.telecom?.find((contact) => contact.system === 'phone')?.value;
      if (!phone || !patient) {
        alert.error('No phone number on file for this patient.');
        return false;
      }

      setPendingAction('sendCherryLink');
      try {
        const input: SendSmsParams = {
          phoneNumber: phone,
          message: `Here's your Cherry financing application: ${CHERRY_APPLICATION_URL}`,
          recipient: getReferenceString(patient),
        };
        await fetchBot(medplum, 'send-sms-message', input);
        alert.success('Cherry application sent.');
        return true;
      } catch (error) {
        alert.error(`Couldn't send the Cherry link: ${error instanceof Error ? error.message : 'Unknown error'}`);
        return false;
      } finally {
        setPendingAction(undefined);
      }
    },
    [medplum, patient]
  );

  return { handleAction, pendingAction };
}
